/**
 * <AdminDisputesTab />
 *
 * Admin order/dispute management.  Lists every order on the platform
 * with filters; exposes a "Refund" action that calls
 * POST /api/admin/orders/{id}/refund.  Supports both full and partial
 * refunds -- partial uses a small inline prompt.
 */
import * as React from "react";
import { AlertCircle, RefreshCcw, Shield } from "lucide-react";
import { api, extractApiError } from "../../../lib/api";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Card, CardBody, CardHeader } from "../../../components/ui/Card";
import { Input } from "../../../components/ui/Input";
import { Select } from "../../../components/ui/Select";

interface Order {
  id: string;
  buyer_id: string;
  seller_id: string;
  product_id: string;
  product_title: string;
  total_amount: number;
  platform_fee: number;
  seller_payout: number;
  status: string;
  payment_status: string;
  stripe_payment_intent_id?: string | null;
  created_at: string;
}

const STATUS_FILTERS = [
  { value: "", label: "All orders" },
  { value: "PENDING", label: "Pending" },
  { value: "PAID", label: "Paid" },
  { value: "SHIPPED", label: "Shipped" },
  { value: "DELIVERED", label: "Delivered" },
  { value: "CANCELED", label: "Canceled" },
  { value: "REFUNDED", label: "Refunded" },
];

export function AdminDisputesTab() {
  const [orders, setOrders] = React.useState<Order[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get<Order[]>("/admin/orders", {
        params: filter ? { status: filter } : {},
      });
      setOrders(r.data);
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  React.useEffect(() => {
    load();
  }, [load]);

  const afterRefund = (updated: Order) => {
    setOrders((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
  };

  return (
    <div className="flex flex-col gap-4">
      <Card elevated>
        <CardHeader>
          <div>
            <h2 className="text-xl font-bold inline-flex items-center gap-2">
              <Shield className="h-5 w-5" aria-hidden /> Orders & disputes
            </h2>
            <p className="text-ink-muted text-sm mt-1">
              View every order on the platform and issue Stripe refunds.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-48">
              <Select
                label="Status"
                hideLabel
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                options={STATUS_FILTERS}
                data-testid="disputes-status-filter"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={load}
              loading={loading}
              leadingIcon={<RefreshCcw className="h-4 w-4" />}
              data-testid="disputes-refresh"
            >
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          {error && (
            <p
              data-testid="disputes-error"
              className="mb-3 inline-flex items-center gap-1.5 text-brand font-semibold"
            >
              <AlertCircle className="h-4 w-4" /> {error}
            </p>
          )}
          {orders.length === 0 ? (
            <p className="text-ink-muted py-8 text-center" data-testid="disputes-empty">
              {loading ? "Loading orders…" : "No orders found for this filter."}
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-surface-border">
              {orders.map((o) => (
                <OrderRow key={o.id} order={o} onRefunded={afterRefund} />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function statusVariant(s: string): "brand" | "solid" | "outline" | "soft" {
  switch (s) {
    case "REFUNDED":
    case "CANCELED":
      return "brand";
    case "PAID":
    case "SHIPPED":
    case "DELIVERED":
      return "solid";
    default:
      return "outline";
  }
}

function OrderRow({ order, onRefunded }: { order: Order; onRefunded: (o: Order) => void }) {
  const [open, setOpen] = React.useState(false);
  const [amount, setAmount] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  const canRefund =
    order.payment_status === "SUCCEEDED" && order.status !== "REFUNDED";

  const doRefund = async (full: boolean) => {
    setBusy(true);
    setErr(null);
    try {
      const body: any = {};
      if (!full) {
        const n = Number(amount);
        if (!n || n <= 0) {
          setErr("Enter a positive refund amount.");
          setBusy(false);
          return;
        }
        body.amount = n;
      }
      if (reason.trim()) body.reason = reason.trim();
      const r = await api.post<Order>(`/admin/orders/${order.id}/refund`, body);
      onRefunded(r.data);
      setOpen(false);
      setAmount("");
      setReason("");
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="py-4" data-testid={`dispute-row-${order.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-ink truncate">{order.product_title}</span>
            <Badge variant={statusVariant(order.status)} size="sm">
              {order.status}
            </Badge>
            {order.payment_status === "REFUNDED" && (
              <Badge variant="brand" size="sm">Refunded</Badge>
            )}
          </div>
          <div className="text-xs text-ink-muted font-mono mt-1">
            #{order.id.slice(0, 8)} · {new Date(order.created_at).toLocaleString()}
          </div>
          <div className="text-sm mt-1">
            Total <strong>${order.total_amount.toFixed(2)}</strong> · Commission $
            {order.platform_fee.toFixed(2)} · Payout ${order.seller_payout.toFixed(2)}
          </div>
        </div>

        {canRefund && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOpen((o) => !o)}
            data-testid={`dispute-refund-toggle-${order.id}`}
          >
            {open ? "Cancel" : "Refund"}
          </Button>
        )}
      </div>

      {open && canRefund && (
        <div
          className="mt-3 rounded-xl border-2 border-surface-border bg-surface-raised p-4"
          data-testid={`dispute-refund-panel-${order.id}`}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label={`Partial amount (max $${order.total_amount.toFixed(2)})`}
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Leave blank for FULL refund"
              data-testid={`dispute-refund-amount-${order.id}`}
            />
            <Input
              label="Reason (optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Buyer requested refund"
              data-testid={`dispute-refund-reason-${order.id}`}
            />
          </div>
          {err && (
            <p
              className="mt-2 text-sm font-semibold text-brand"
              data-testid={`dispute-refund-error-${order.id}`}
            >
              {err}
            </p>
          )}
          <div className="flex items-center justify-end gap-2 mt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => doRefund(false)}
              loading={busy}
              data-testid={`dispute-refund-partial-${order.id}`}
            >
              Refund partial
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => doRefund(true)}
              loading={busy}
              data-testid={`dispute-refund-full-${order.id}`}
            >
              Refund FULL (${order.total_amount.toFixed(2)})
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}
